import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import {
  makeStyles,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  MoreVert as MoreVertIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowCampaignModal from "../../components/FlowCampaignModal";
import { i18n } from "../../translate/i18n";
import { listFlowBuilders } from "../../services/flowBuilders.api";
import { listFlowCampaigns, deleteFlowCampaign } from "../../services/flowCampaigns.api";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { FLOW_CHANNELS, FLOW_CHANNEL_META, channelOfFlow } from "../../constants/flowChannels";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    padding: theme.spacing(1, 2),
  },
  fila: { cursor: "pointer" },
  chipSuave: { marginRight: 4, marginBottom: 2, height: 22, fontSize: 12 },
}));

const t = (clave, defecto) => i18n.t(clave) || defecto;

/**
 * Campañas de flujo: una palabra clave arranca un flujo en ciertas conexiones.
 * Mismo modelo que la lista de flujos (doc 0438 Parte VII): pestañas por canal, chip de canal, frases y
 * conexiones visibles, y clic en la fila para editar. El formulario vive en FlowCampaignModal.
 */
const FlowCampaigns = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { whatsApps } = useContext(WhatsAppsContext);

  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [flows, setFlows] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [canalFiltro, setCanalFiltro] = useState("todos");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuCampaign, setMenuCampaign] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    fetchFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const { data } = await listFlowCampaigns();
      setCampaigns(data.records || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFlows = async () => {
    try {
      const { data } = await listFlowBuilders({ active: true });
      setFlows(data.records || []);
    } catch (err) {
      toastError(err);
    }
  };

  const flujoDe = useMemo(() => new Map(flows.map((f) => [Number(f.id), f])), [flows]);
  const conexionDe = useMemo(() => new Map((whatsApps || []).map((w) => [Number(w.id), w])), [whatsApps]);
  const canalDe = (campaign) => channelOfFlow(flujoDe.get(Number(campaign?.flowId))?.provider);

  const filtradas = campaigns.filter(
    (c) =>
      (c.name || "").toLowerCase().includes(searchParam) &&
      (canalFiltro === "todos" || canalDe(c) === canalFiltro)
  );
  const pagina = filtradas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const abrir = (campaign) => {
    setEditing(campaign);
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleDeleteCampaign = async () => {
    if (!deletingCampaign) return;
    try {
      await deleteFlowCampaign(deletingCampaign.id);
      toast.success(t("flowcampaigns.toasts.deleted", "Campaña eliminada exitosamente"));
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmModalOpen(false);
      setDeletingCampaign(null);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{t("flowcampaigns.title", "Campañas de Flujo")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={() => abrir(null)} startIcon={<AddIcon />}>
            {t("flowcampaigns.buttons.add", "Nueva Campaña")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={canalFiltro}
          onChange={(_, v) => { setCanalFiltro(v); setPage(0); }}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          style={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Tab value="todos" label="Todas" />
          {FLOW_CHANNELS.map((c) => (
            <Tab
              key={c}
              value={c}
              label={
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <img src={FLOW_CHANNEL_META[c].icon} alt="" style={{ width: 18, height: 18 }} />
                  {FLOW_CHANNEL_META[c].label}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <TextField
            placeholder={t("flowcampaigns.searchPlaceholder", "Buscar campañas...")}
            type="search"
            size="small"
            variant="outlined"
            value={searchParam}
            onChange={(e) => { setSearchParam(e.target.value.toLowerCase()); setPage(0); }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>) }}
          />
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.customTableCell}>{t("flowcampaigns.table.name", "Nombre")}</TableCell>
              <TableCell className={classes.customTableCell}>{t("flowcampaigns.table.flow", "Flujo")}</TableCell>
              <TableCell className={classes.customTableCell}>{t("flowcampaigns.table.phrases", "Frases")}</TableCell>
              <TableCell className={classes.customTableCell}>Conexiones</TableCell>
              <TableCell className={classes.customTableCell}>{t("flowcampaigns.table.status", "Estado")}</TableCell>
              <TableCell className={classes.customTableCell} align="center">{t("flowcampaigns.table.actions", "Acciones")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={6} />}
            {!loading && pagina.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {searchParam || canalFiltro !== "todos" ? "No hay campañas con este filtro" : "No hay campañas creadas"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              pagina.map((c) => {
                const canal = FLOW_CHANNEL_META[canalDe(c)];
                const frases = c.phrase || [];
                const conexiones = (c.whatsappIds || []).map((id) => conexionDe.get(Number(id))?.name || `#${id}`);
                return (
                  <TableRow key={c.id} hover className={classes.fila} onClick={() => abrir(c)}>
                    <TableCell className={classes.customTableCell}>
                      <Typography variant="body2" style={{ fontWeight: 500, color: theme.palette.primary.main }}>{c.name || "Sin nombre"}</Typography>
                    </TableCell>
                    <TableCell className={classes.customTableCell}>
                      <Typography variant="body2">{c.flowName || flujoDe.get(Number(c.flowId))?.name || `Flujo #${c.flowId}`}</Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        avatar={<img src={canal.icon} alt="" style={{ width: 16, height: 16 }} />}
                        label={canal.label}
                        style={{ marginTop: 4, borderColor: canal.color }}
                      />
                    </TableCell>
                    <TableCell className={classes.customTableCell}>
                      {frases.slice(0, 2).map((p, i) => (
                        <Chip key={i} size="small" className={classes.chipSuave} label={`«${p.text}»`} />
                      ))}
                      {frases.length > 2 && (
                        <Tooltip title={frases.slice(2).map((p) => p.text).join(" · ")}>
                          <Chip size="small" className={classes.chipSuave} label={`+${frases.length - 2}`} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className={classes.customTableCell}>
                      {conexiones.slice(0, 2).map((n, i) => (
                        <Chip key={i} size="small" variant="outlined" className={classes.chipSuave} label={n} />
                      ))}
                      {conexiones.length > 2 && (
                        <Tooltip title={conexiones.slice(2).join(" · ")}>
                          <Chip size="small" variant="outlined" className={classes.chipSuave} label={`+${conexiones.length - 2}`} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className={classes.customTableCell}>
                      <Chip
                        label={c.status ? t("flowcampaigns.status.active", "Activa") : t("flowcampaigns.status.inactive", "Inactiva")}
                        color={c.status ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell className={classes.customTableCell} align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setMenuCampaign(c); }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtradas.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          labelRowsPerPage={t("flowcampaigns.rowsPerPage", "Filas por página:")}
        />
        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
          <MenuItem onClick={() => { setMenuAnchorEl(null); abrir(menuCampaign); }}>
            <EditIcon style={{ marginRight: 8, fontSize: 18 }} />
            {t("flowcampaigns.actions.edit", "Editar")}
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchorEl(null); setDeletingCampaign(menuCampaign); setConfirmModalOpen(true); }}>
            <DeleteOutlineIcon style={{ marginRight: 8, fontSize: 18 }} />
            {t("flowcampaigns.actions.delete", "Eliminar")}
          </MenuItem>
        </Menu>
        <ConfirmationModal
          title={t("flowcampaigns.confirmationModal.deleteTitle", "Eliminar Campaña")}
          open={confirmModalOpen}
          onClose={() => { setConfirmModalOpen(false); setDeletingCampaign(null); }}
          onConfirm={handleDeleteCampaign}
        >
          {t("flowcampaigns.confirmationModal.deleteMessage", "¿Está seguro que desea eliminar esta campaña?")}
        </ConfirmationModal>
        <FlowCampaignModal
          open={modalOpen}
          campaign={editing}
          flows={flows}
          whatsApps={whatsApps}
          onClose={cerrarModal}
          onSaved={() => { cerrarModal(); fetchCampaigns(); }}
        />
      </Paper>
    </MainContainer>
  );
};

export default FlowCampaigns;
